import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { CompanyLogo } from "../../../../../components/CompanyLogo";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import { useLocation, useNavigate, NavigateFunction } from "react-router-dom";

interface OrganizationHierarchyNode {
  id: string;
  name: string;
  domain?: string;
  parent?: OrganizationHierarchyNode;
}

interface NodeDisplayContentProps {
  hq: OrganizationHierarchyNode;
  isExternalLink?: boolean;
}

interface HierarchyNodeItemProps {
  hq: OrganizationHierarchyNode;
  isSingleTopmostNode: boolean;
  isParentInHierarchy: boolean;
  isChildInHierarchy: boolean;
  navigate: NavigateFunction;
  locationSearch: string;
}

const HierarchyBoxStyles = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  p: 3,
  width: "100%",
  boxSizing: "border-box",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "action.hover",
    transform: "translateY(-2px)",
  },
} as const;

const NodeDisplayContent: React.FC<NodeDisplayContentProps> = React.memo(
  ({ hq, isExternalLink }) => (
    <Stack direction="row" alignItems="center" gap={2}>
      <CompanyLogo domain={hq.domain || ""} size={50} variant="rounded" />
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={isExternalLink ? { color: "primary.main" } : {}}
        >
          {hq.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {hq.domain}
        </Typography>
      </Box>
    </Stack>
  )
);

NodeDisplayContent.displayName = "NodeDisplayContent";

const HierarchyNodeItem: React.FC<HierarchyNodeItemProps> = React.memo(
  ({
    hq,
    isSingleTopmostNode,
    isParentInHierarchy,
    isChildInHierarchy,
    navigate,
    locationSearch,
  }) => {
    const domainUrl =
      hq.domain && !hq.domain.startsWith("http") && !hq.domain.startsWith("//")
        ? `//${hq.domain}`
        : hq.domain;

    if (isSingleTopmostNode && domainUrl) {
      return (
        <Link
          href={domainUrl}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          sx={{ textDecoration: "none", width: "100%", display: "block" }}
        >
          <Box sx={{ ...HierarchyBoxStyles }}>
            <NodeDisplayContent hq={hq} isExternalLink />
          </Box>
        </Link>
      );
    } else if (isParentInHierarchy && hq.id) {
      return (
        <Box
          onClick={() =>
            navigate(`/catalog/companies/${hq.id}${locationSearch}`)
          }
          sx={{ ...HierarchyBoxStyles, cursor: "pointer" }}
        >
          <NodeDisplayContent hq={hq} />
        </Box>
      );
    } else if (isChildInHierarchy && domainUrl) {
      return (
        <Link
          href={domainUrl}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          sx={{ textDecoration: "none", width: "100%", display: "block" }}
        >
          <Box sx={{ ...HierarchyBoxStyles }}>
            <NodeDisplayContent hq={hq} isExternalLink />
          </Box>
        </Link>
      );
    }
    return (
      <Box sx={{ ...HierarchyBoxStyles }}>
        <NodeDisplayContent hq={hq} />
      </Box>
    );
  }
);

HierarchyNodeItem.displayName = "HierarchyNodeItem";

export const GlobalHeadquarters: React.FC = React.memo(() => {
  const { company } = useCompanyPageStore();
  const navigate = useNavigate();
  const location = useLocation();

  const companyHierarchyNode: OrganizationHierarchyNode | null = useMemo(() => {
    if (!company) return null;

    const companyFields: OrganizationHierarchyNode = {
      name: company.name,
      domain: company.url,
      id: company.hgId,
    };

    let parentNode: OrganizationHierarchyNode | undefined;
    if (company.parent && company.parent.hgId !== company.hgId) {
      parentNode = {
        name: company.parent.name || "",
        domain: company.parent.url || "",
        id: company.parent.hgId || "",
      };
    }

    if (!parentNode) return companyFields;

    return { ...companyFields, parent: parentNode };
  }, [company]);

  const nodesToDisplay: OrganizationHierarchyNode[] = useMemo(() => {
    if (!companyHierarchyNode) return [];

    return [
      ...(companyHierarchyNode.parent ? [companyHierarchyNode.parent] : []),
      companyHierarchyNode,
    ];
  }, [companyHierarchyNode]);

  if (!companyHierarchyNode) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Global Headquarters (GHQ)
      </Typography>

      <Stack spacing={1} alignItems="center">
        {nodesToDisplay.map((hq: OrganizationHierarchyNode, index: number) => {
          const isSingleTopmostNode =
            nodesToDisplay.length === 1 && index === 0;
          const isParentInHierarchy = nodesToDisplay.length > 1 && index === 0;
          const isChildInHierarchy = nodesToDisplay.length > 1 && index === 1;

          return (
            <React.Fragment key={`${hq.id}-${index}`}>
              <HierarchyNodeItem
                hq={hq}
                isSingleTopmostNode={isSingleTopmostNode}
                isParentInHierarchy={isParentInHierarchy}
                isChildInHierarchy={isChildInHierarchy}
                navigate={navigate}
                locationSearch={location.search}
              />
              {companyHierarchyNode.parent &&
                index === 0 &&
                nodesToDisplay.length > 1 && (
                  <ArrowUpward sx={{ color: "text.secondary", my: 1.5 }} />
                )}
              {!companyHierarchyNode.parent &&
                nodesToDisplay.length === 1 &&
                index === 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ my: 1.5, textAlign: "center" }}
                  >
                    This company is the topmost parent of its corporate
                    hierarchy
                  </Typography>
                )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
});

GlobalHeadquarters.displayName = "GlobalHeadquarters";
